import React, { useRef, useState } from "react";
import { Download, X } from "lucide-react";

const SIZES = {
  "ig-post": { w: 1080, h: 1350, label: "Instagram Post" },
  "ig-story": { w: 1080, h: 1920, label: "IG / FB Story" },
  "x-link": { w: 1200, h: 675, label: "X / LinkedIn / FB" },
};

export const ShareCardGenerator = ({ story, categoryColor = "#F42A41", onClose }) => {
  const [size, setSize] = useState("ig-post");
  const canvasRef = useRef(null);

  const draw = async (key) => {
    const s = SIZES[key];
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = s.w;
    canvas.height = s.h;
    const ctx = canvas.getContext("2d");
    // bg
    ctx.fillStyle = "#FAF8F3";
    ctx.fillRect(0, 0, s.w, s.h);
    // top color band
    ctx.fillStyle = categoryColor;
    ctx.fillRect(0, 0, s.w, s.h * 0.08);
    // dot logo
    ctx.fillStyle = "#0F0F12";
    ctx.font = `900 ${Math.round(s.w * 0.06)}px Bricolage Grotesque, Inter, sans-serif`;
    ctx.fillText("Newsgram", s.w * 0.05, s.h * 0.17);
    ctx.beginPath();
    ctx.arc(s.w * 0.05 + ctx.measureText("Newsgram").width + 18, s.h * 0.16, s.w * 0.012, 0, Math.PI * 2);
    ctx.fillStyle = "#F42A41";
    ctx.fill();
    // headline
    ctx.fillStyle = "#0F0F12";
    const headlineSize = s.w * (s.w >= s.h ? 0.055 : 0.07);
    ctx.font = `800 ${headlineSize}px Bricolage Grotesque, Inter, sans-serif`;
    wrap(ctx, story.title, s.w * 0.05, s.h * 0.28, s.w * 0.9, headlineSize * 1.1);
    // gist
    if (story.gist?.[0]) {
      ctx.fillStyle = "#52525B";
      const gistSize = s.w * 0.03;
      ctx.font = `500 ${gistSize}px Inter, sans-serif`;
      wrap(ctx, "• " + story.gist[0], s.w * 0.05, s.h * (s.w >= s.h ? 0.62 : 0.72), s.w * 0.9, gistSize * 1.3);
    }
    // footer: source
    ctx.fillStyle = "#0F0F12";
    const footer = s.w * 0.025;
    ctx.font = `700 ${footer}px Inter, sans-serif`;
    ctx.fillText("@newsgram.bd  •  newsgram.bd", s.w * 0.05, s.h * 0.95);
    const srcName = story.sources?.[0]?.publication ? `Source: ${story.sources[0].publication}` : "";
    if (srcName) {
      ctx.textAlign = "right";
      ctx.fillText(srcName, s.w * 0.95, s.h * 0.95);
      ctx.textAlign = "left";
    }
  };

  const wrap = (ctx, text, x, y, maxW, lineH) => {
    const words = (text || "").split(" ");
    let line = "", ly = y;
    for (const w of words) {
      const test = line + w + " ";
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line, x, ly);
        line = w + " ";
        ly += lineH;
      } else line = test;
    }
    ctx.fillText(line, x, ly);
  };

  React.useEffect(() => { draw(size); }, [size]); // eslint-disable-line

  const download = () => {
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = `newsgram-${story.slug}-${size}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-card-modal">
      <div className="bg-card rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-display font-extrabold text-xl">Share card generator</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-muted flex items-center justify-center" data-testid="share-card-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 flex flex-col gap-4">
          <div className="flex gap-2 flex-wrap">
            {Object.entries(SIZES).map(([k, v]) => (
              <button key={k} onClick={() => setSize(k)} data-testid={`share-size-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border ${size === k ? "bg-foreground text-background border-foreground" : "bg-background text-foreground border-border"}`}>
                {v.label} ({v.w}×{v.h})
              </button>
            ))}
          </div>
          <div className="rounded-2xl overflow-hidden bg-muted flex items-center justify-center p-4">
            <canvas ref={canvasRef} className="max-w-full max-h-[55vh] rounded-xl bg-white" />
          </div>
          <button onClick={download} className="h-11 rounded-full bg-signal text-white font-bold inline-flex items-center justify-center gap-2" data-testid="share-card-download">
            <Download className="w-4 h-4" /> Download PNG
          </button>
        </div>
      </div>
    </div>
  );
};
